/* eslint-disable global-require */
import { Grommet } from 'grommet'
import React, { useEffect } from 'react'
import WindowSizeListener from 'react-window-size-listener'
import { QueryClientProvider } from 'react-query'
import { BrowserRouter, useLocation } from 'react-router-dom'
import Cookies from 'js-cookie'
import { inject } from '@vercel/analytics'
import { ToastContainer } from 'react-toastify'
import languages from '@cospired/i18n-iso-languages'
import countries from 'i18n-iso-countries'
import 'react-toastify/dist/ReactToastify.css'
import 'react-loading-skeleton/dist/skeleton.css'
import { SkeletonTheme } from 'react-loading-skeleton'
import * as mobx from 'mobx'

import 'react-calendar/dist/Calendar.css'
import utilsMobx from './mobx/utils'
import './utils/i18n'
import queryClient from './utils/clientProvider'
import Router from './routing'
import api from './utils/apiUtils'
import ModalVideo from './utils/components/Modals/ModalVideo'
import ModalAlert from './utils/components/Modals/ModalAlert'
import ModalCard from './utils/components/Modals/ModalCart'
import ModalPopUp from './utils/components/Modals/ModalPopUp'
import ModalAddFavorite from './utils/components/Modals/ModalAddFavorite'
import ModalDefault from './utils/components/Modals/ModalDefault'
import ModalEnroll from './utils/components/Modals/ModalEnroll'
import socketServiceInstance, { SocketService } from './sockets'
import FaqChatbot from './utils/components/FaqChatbot'
import WhatsNewBanner from './utils/components/WhatsNewBanner'
import InviteModal from './utils/components/CampaignInvite/InviteModal'
import userMobx from './mobx/user'
import { ClientI, TypeClient } from './types-project/Client'
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
// @ts-ignore
window.mobx = mobx

// Routes pre-auth ou immersives sur lesquelles on masque les overlays
// globaux (chatbot FAQ, bandeau "Quoi de neuf"). Ces composants sont
// pertinents uniquement pour les marques deja connectees.
const HIDE_OVERLAYS_ON = ['/welcome', '/login', '/signup']

// Routes ou le bouton flottant du chatbot FAQ chevauche l UI (ex: bouton
// d envoi de la messagerie). On masque uniquement le chatbot, pas le reste.
// Concerne toutes les pages embarquant un chat avec bouton "envoyer" :
// messagerie, profil createur, et detail d'une candidature (casting).
const HIDE_CHATBOT_ON = ['/dashboard/messaging', '/dashboard/profil-vogzter']

// Detail d'un casting/deal (/dashboard/castings/:id et sous-routes) : la page
// embarque la messagerie (bouton "envoyer") sur les deals prives, le bouton
// flottant du chatbot la chevauche. On ne masque QUE le detail (presence d'un
// id apres /castings/), pas la liste /dashboard/castings.
const CASTING_DETAIL_RE = /^\/dashboard\/castings\/[^/]+/

// Hook re-render-safe pour detecter (de-)connexion sans reload :
// - check du cookie 'token' (source de verite cote API, cf apiUtils)
// - listener focus/storage pour rafraichir au retour sur l onglet ou
//   apres logout dans un autre tab
const useIsLogged = (): boolean => {
  const [logged, setLogged] = React.useState<boolean>(() => !!Cookies.get('token'))

  React.useEffect(() => {
    const refresh = () => setLogged(!!Cookies.get('token'))
    window.addEventListener('focus', refresh)
    window.addEventListener('storage', refresh)
    // Ping toutes les 5s : couvre le login/logout dans le meme onglet
    // (les cookies n emettent pas d evenement). Cout negligeable.
    const interval = window.setInterval(refresh, 5000)
    return () => {
      window.removeEventListener('focus', refresh)
      window.removeEventListener('storage', refresh)
      window.clearInterval(interval)
    }
  }, [])

  return logged
}

const ConditionalOverlays: React.FC = () => {
  const location = useLocation()
  const isLogged = useIsLogged()
  // 1. Pas d overlay sur les pages publiques pre-auth (welcome/login/signup)
  if (HIDE_OVERLAYS_ON.some((p) => location.pathname.startsWith(p))) return null
  // 2. Pas d overlay tant que la marque n est pas connectee. Evite de
  //    polluer les pages publiques (cgv, premium, profils publics, etc)
  //    avec le bandeau changelog ou le chatbot.
  if (!isLogged) return null
  const hideChatbot =
    HIDE_CHATBOT_ON.some((p) => location.pathname.startsWith(p)) ||
    // Le detail d'une candidature (/dashboard/castings/:id/briefapplication/:id)
    // contient aussi un chat : le segment est en milieu d'URL d'ou le includes.
    location.pathname.includes('/briefapplication/') ||
    // Detail d'un casting/deal prive (messagerie embarquee).
    CASTING_DETAIL_RE.test(location.pathname)
  return (
    <>
      {!hideChatbot && <FaqChatbot />}
      <WhatsNewBanner />
      <InviteModal />
    </>
  )
}

const App: React.FC = () => {
  const theme = {
    global: {
      colors: {
        myColor: '#808191',
        'background-contrast': {
          dark: '#FFFFFF14',
          light: '#0000000A',
        },
        'active-background': 'background-contrast',
        'active-text': 'red',
        icon: 'text',
        // focus color is an important indication for keyboard navigation accessibility,
        // it will be an ill advice to set it to undefined and remove focus
        focus: '#000',
        text: {
          font: {
            size: '10px',
          },
          dark: '#C0CADC',
          light: '#444444',
        },
      },
    },
    formField: {
      margin: 'small',
    },
    button: {
      default: {
        color: '#fff',
        border: {
          color: '#5777FF',
          width: '1px',
        },
        background: {
          color: '#5777FF',
        },
        font: {
          weight: 700,
        },
        text: {
          size: {
            medium: '10px',
          },
        },
      },
      hover: {
        default: {
          background: {
            color: '#fff',
          },
          color: '#5777FF',
        },
        secondary: {
          border: {
            width: '3px',
          },
          padding: {
            horizontal: '9px',
            vertical: '3px',
          },
        },
      },
      active: {
        background: {
          color: '#5777FF',
        },
        color: '#5777FF',
        secondary: {
          border: {
            color: 'transparent',
          },
        },
      },
    },
    checkBox: {
      size: '18px',
      hover: {
        border: {
          color: 'grey',
        },
      },
      icon: {
        extend: `color:#5777FF`,
      },
      toggle: {
        color: { dark: '#5777FF', light: '#d9d9d9' },
      },
    },
  }

  useEffect(() => {
    api.interceptors.request.use(
      async (config) => {
        const token = userMobx.getToken()
        if (token) {
          config.headers = {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          }
        }
        return config
      },
      (error) => {
        Promise.reject(error)
      }
    )
    inject()
    const userInfos = userMobx.getUserInfos() as ClientI
    if (userInfos !== null || Object.keys(userMobx.me.data ?? {}).length > 0) {
      if (
        userMobx.me.data?._company?.type === TypeClient.AGENCY ??
        userInfos?._company.type === TypeClient.AGENCY
      ) {
        if (userMobx.getBrandSession() !== null)
          socketServiceInstance.emit(`brand`, userMobx.getBrandSession()?._id)
      } else {
        socketServiceInstance.emit(
          `brand`,
          userMobx.me.data?.brand?._id ?? userInfos.brand?._id
        )
      }
    }
    languages.registerLocale(
      require('@cospired/i18n-iso-languages/langs/en.json')
    )
    languages.registerLocale(
      require('@cospired/i18n-iso-languages/langs/fr.json')
    )
    countries.registerLocale(require('i18n-iso-countries/langs/en.json'))
    countries.registerLocale(require('i18n-iso-countries/langs/fr.json'))
  }, [])
  

  return (
    <QueryClientProvider client={queryClient}>
      {/* @ts-ignore */}
      <Grommet theme={theme}>
        <WindowSizeListener
          onResize={(windowSize) => {
            utilsMobx.handleWindow(
              windowSize.windowWidth,
              windowSize.windowHeight
            )
            utilsMobx.handleOpen((windowSize.windowWidth as number) <= 1440)
          }}
        />
        <SkeletonTheme baseColor="#202020" highlightColor="#444">
          <BrowserRouter>
            <Router />
            <ConditionalOverlays />
            {/* Dans le Router pour que le contenu cliquable des toasts (ex. toast
                "nouveau message") puisse utiliser la navigation SPA (useNavigate). */}
            <ToastContainer />
          </BrowserRouter>
        </SkeletonTheme>
      </Grommet>
      <ModalAlert />
      <ModalCard />
      <ModalPopUp />
      <ModalAddFavorite />
      <ModalEnroll />
      <ModalVideo />
      <ModalDefault />
    </QueryClientProvider>
  )
}
export default App
